/**
 * Mouse drag-to-scroll for the carousel and slideshow tracks (#99). Loaded from the layout
 * before the section and snippet scripts (deferred scripts run in document order), so an
 * element attaches it from its connectedCallback: `window.theme.dragScroll(track, options)`.
 *
 * Mouse pointers only: touch and pen keep the native scroll. A press on the track that
 * moves past the threshold starts the drag: the track follows the pointer through
 * scrollLeft with the snap and the smooth behaviour off (inline, restored once settled)
 * and the pointer captured; while the button is down no selection starts and no image or
 * link enters a native drag, and the moves and the release are read on the window, so a
 * fast flick that leaves the track before its first move still drags it. On release the
 * track settles on the nearest snap point, or on the position `onRelease(delta)` returns,
 * smooth, instant under reduced motion; the snap only comes back once that scroll has
 * ended so it never fights the settle. The click the browser fires after a drag is
 * swallowed once, wherever it lands; a press and release under the threshold stays a
 * click and keeps its focus. State on the track: `data-drag-scroll` while it overflows
 * (grab cursor), `data-drag="active"` while it follows the pointer, then
 * `data-drag="settling"` until the snap point is reached; the cursor rules sit in critical.css.
 */
(() => {
  const THRESHOLD = 5;
  const STALL_FRAMES = 6;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* One mouse, one drag at a time: the click that follows a drag is swallowed by a single
     window listener, whichever track the drag came from. */
  let swallowClick = false;
  window.addEventListener('click', (event) => {
    if (!swallowClick) return;
    swallowClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  /* Inset of the snapport on one side: the scroll padding when it resolves to a length,
     else the track's own padding (the theme's tracks keep the two equal, and a scroll
     padding written with a percentage stays an expression in the computed style). */
  function snapInset(style, side) {
    const value = style[`scrollPadding${side}`];
    return /^[\d.]+px$/.test(value) ? parseFloat(value) : parseFloat(style[`padding${side}`]) || 0;
  }

  /* Scroll position aligning one child on its snap alignment (start or center), clamped
     to the scroll range. */
  function snapPosition(track, child) {
    const align = getComputedStyle(child).scrollSnapAlign.split(' ')[0];
    if (align === 'none') return null;
    const style = getComputedStyle(track);
    const padStart = snapInset(style, 'Left');
    const padEnd = snapInset(style, 'Right');
    const port = track.getBoundingClientRect();
    const box = child.getBoundingClientRect();
    const target = align === 'center'
      ? box.left + box.width / 2 - (port.left + padStart + (port.width - padStart - padEnd) / 2)
      : box.left - (port.left + padStart);
    return Math.min(Math.max(0, track.scrollLeft + target), track.scrollWidth - track.clientWidth);
  }

  function nearestSnap(track) {
    let best = null;
    for (const child of track.children) {
      const position = snapPosition(track, child);
      if (position === null) continue;
      if (best === null || Math.abs(position - track.scrollLeft) < Math.abs(best - track.scrollLeft)) best = position;
    }
    return best ?? track.scrollLeft;
  }

  /* Calls back once the smooth scroll has reached `target`, or has stood still for a few
     frames (a target the layout no longer allows). Frame polling rather than scrollend:
     that event also closes the drag's own scroll sequence right after the release. */
  function whenSettled(track, target, callback) {
    let last = track.scrollLeft;
    let still = 0;
    const check = () => {
      if (Math.abs(track.scrollLeft - target) < 1) return callback();
      if (track.scrollLeft === last) {
        if (++still >= STALL_FRAMES) return callback();
      } else {
        still = 0;
        last = track.scrollLeft;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  }

  function dragScroll(track, { onStart, onRelease } = {}) {
    let pointerId = null;
    let startX = 0;
    let startLeft = 0;
    let active = false;
    // Counts the drag starts and the settles: a settle only restores the track if no
    // newer drag or settle has taken over meanwhile.
    let sequence = 0;

    const restore = () => {
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
      track.removeAttribute('data-drag');
    };

    const settle = (target) => {
      const own = ++sequence;
      track.dataset.drag = 'settling';
      const finish = () => { if (own === sequence) restore(); };
      if (Math.abs(target - track.scrollLeft) < 1) return finish();
      if (reduced.matches) {
        track.scrollTo({ left: target, behavior: 'instant' });
        return finish();
      }
      track.scrollTo({ left: target, behavior: 'smooth' });
      whenSettled(track, target, finish);
    };

    const move = (event) => {
      if (event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (!active) {
        if (Math.abs(delta) < THRESHOLD) return;
        active = true;
        sequence += 1;
        // Capture keeps the moves on the track once the pointer leaves it; it throws for a
        // pointer the browser no longer tracks, which changes nothing else.
        try { track.setPointerCapture(pointerId); } catch { /* no capture, the drag goes on */ }
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.dataset.drag = 'active';
        onStart?.();
      }
      track.scrollLeft = startLeft - delta;
    };

    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
      if (!active) return;
      active = false;
      // The click of this release is swallowed; the flag clears once that event has fired.
      swallowClick = true;
      setTimeout(() => { swallowClick = false; }, 0);
      const target = onRelease?.(event.clientX - startX);
      settle(typeof target === 'number' ? target : nearestSnap(track));
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || pointerId !== null) return;
      swallowClick = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      active = false;
      window.addEventListener('pointermove', move, true);
      window.addEventListener('pointerup', release, true);
      window.addEventListener('pointercancel', release, true);
    });
    // A capture the browser takes away (the pointer gone without a pointerup) ends the drag
    // the same way; after a pointerup the release has already run and this returns at once.
    track.addEventListener('lostpointercapture', release);

    // While the button is down on the track: no selection starts, no image or link enters
    // a native drag. The mousedown default stays, so a click still focuses its target.
    const blockWhilePressed = (event) => { if (pointerId !== null) event.preventDefault(); };
    track.addEventListener('selectstart', blockWhilePressed);
    track.addEventListener('dragstart', blockWhilePressed);

    const observe = () => track.toggleAttribute('data-drag-scroll', track.scrollWidth - track.clientWidth > 1);
    new ResizeObserver(observe).observe(track);
    observe();
  }

  window.theme = window.theme || {};
  window.theme.dragScroll = dragScroll;
})();
